// src/pages/GerarDocumentos/index.js
import React, { useState } from 'react';
import { useClient } from '../../contexts/ClientContext';
import { useAuth } from '../../contexts/AuthContext';
import AnexarUnificadoDialog from '../../components/dialogs/AnexarUnificadoDialog';

import useDocumentConfig from './hooks/useDocumentConfig';
import useDocumentGeneration from './hooks/useDocumentGeneration';
import useGeneratedDocuments from './hooks/useGeneratedDocuments';

import ConfigSection from './components/ConfigSection';
import ResultadoSection from './components/ResultadoSection';
import DocumentosGeradosTable from './components/DocumentosGeradosTable';
import VerTextoDialog from './components/VerTextoDialog';
import AlterarDocumentoDialog from './components/AlterarDocumentoDialog';

import './GerarDocumentos.css';

const GerarDocumentos = () => {
  const { user } = useAuth();
  const { clienteAtual } = useClient();

  const configHook = useDocumentConfig();

  // ── Anexos transcritos (substituem FileUpload + MeusDocumentos) ──────────────
  const [anexosTranscritos, setAnexosTranscritos] = useState([]);
  const [dialogAnexo, setDialogAnexo]             = useState(false);

  const adicionarAnexos = (novos) => setAnexosTranscritos(prev => [...prev, ...novos]);
  const removerAnexo    = (idx)  => setAnexosTranscritos(prev => prev.filter((_, i) => i !== idx));

  const generation = useDocumentGeneration({
    config: configHook.config,
    anexosTranscritos,
    clienteAtual,
  });

  const history = useGeneratedDocuments();

  const handleTipoChange = (tipo) => {
    generation.handleTipoDocumentoChange(tipo);
    setAnexosTranscritos([]);
  };

  const displayError = generation.error || history.error;

  return (
    <div className="gerar-documentos-container">
      <div className="gerar-documentos-header">
        <h2>📄 Gerar Documentos</h2>
        <p>Geração automática e padronizada de documentos jurídicos.</p>
        {user && (user.nome || user.documento) && (
          <div className="user-info">
            <small>
              Advogado: <strong>{user.nome || 'Não informado'}</strong> |
              OA: <strong>{user.documento || 'Não informado'}</strong>
            </small>
          </div>
        )}
      </div>

      <div className="gerar-documentos-content">
        <div className="gerar-form">

          <div className="form-group">
            <label className="field-label"><span>📋</span> Tipo de Documento</label>
            <select
              value={generation.tipoDocumento}
              onChange={(e) => handleTipoChange(e.target.value)}
              className="subtype-select"
            >
              <option value="procuracao">Procuração Forense</option>
              <option value="outros">Outros (especificar)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="field-label">
              <span>🔢</span> Código do Cliente {generation.tipoDocumento === 'procuracao' ? '' : '(opcional)'}
            </label>
            <input
              type="text"
              value={generation.codCliente}
              onChange={(e) => generation.setCodCliente(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="00000"
              className="code-input"
              inputMode="numeric"
            />
            <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
              {clienteAtual?.codCliente
                ? '✓ Preenchido automaticamente com o cliente da sessão'
                : 'Será salvo junto com o documento quando informado'}
            </small>
          </div>

          {generation.tipoDocumento === 'outros' && (
            <>
              <div className="form-group">
                <label className="field-label"><span>📝</span> Nome do Documento *</label>
                <input
                  type="text"
                  value={generation.nomeDocumentoCustomizado}
                  onChange={(e) => generation.setNomeDocumentoCustomizado(e.target.value)}
                  placeholder="Ex: Recurso de Agravo, Ação Executiva, Procedimento Cautelar"
                  className="code-input"
                />
              </div>

              {/* ── Secção de Anexos Unificada ── */}
              <div className="form-group">
                <label className="field-label"><span>📎</span> Anexos (opcional)</label>

                <button
                  type="button"
                  className="file-upload-label"
                  onClick={() => setDialogAnexo(true)}
                  disabled={generation.loading}
                  style={{ cursor: generation.loading ? 'not-allowed' : 'pointer' }}
                >
                  <span>📎</span> Adicionar Documento ou Áudio
                </button>

                {anexosTranscritos.length > 0 && (
                  <div className="arquivos-anexados">
                    {anexosTranscritos.map((anexo, idx) => (
                      <div key={idx} className="arquivo-item">
                        <span className="arquivo-icon">📄</span>
                        <span className="arquivo-nome">{anexo.nome}</span>
                        <span className="arquivo-tamanho">
                          ({Math.round(anexo.conteudo.length / 5)} palavras ~)
                        </span>
                        <button
                          type="button"
                          className="btn-remover-arquivo"
                          onClick={() => removerAnexo(idx)}
                          disabled={generation.loading}
                        >
                          ✖
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                  Suporta PDF, imagens (OCR) e ficheiros de áudio (transcrição automática)
                </small>
              </div>

              <div className="form-group">
                <label className="field-label"><span>💬</span> Instruções Adicionais (opcional)</label>
                <textarea
                  value={generation.instrucoesAdicionais}
                  onChange={(e) => generation.setInstrucoesAdicionais(e.target.value)}
                  placeholder="Digite instruções específicas..."
                  className="instrucoes-textarea"
                  rows={4}
                />
              </div>
            </>
          )}

          <div className="config-toggle">
            <button
              className="toggle-config-btn"
              onClick={() => configHook.setShowConfig(!configHook.showConfig)}
            >
              ⚙️ {configHook.showConfig ? 'Ocultar' : 'Mostrar'} Configurações
            </button>
          </div>

          {configHook.showConfig && (
            <ConfigSection
              config={configHook.config}
              handleConfigChange={configHook.handleConfigChange}
              tipoDocumento={generation.tipoDocumento}
            />
          )}

          <button
            className="gerar-btn"
            onClick={generation.handleGerarDocumento}
            disabled={generation.loading || !generation.isFormValid()}
          >
            {generation.loading ? '⏳ Gerando...' : '📄 GERAR DOCUMENTO'}
          </button>

          {displayError && <div className="error-message">{displayError}</div>}
        </div>

        <ResultadoSection
          loading={generation.loading}
          responseText={generation.responseText}
          documentData={generation.documentData}
          tipoDocumento={generation.tipoDocumento}
          arquivosAnexados={anexosTranscritos}
          copyToClipboard={generation.copyToClipboard}
          downloadDocument={generation.downloadDocument}
          gerarDocx={generation.gerarDocx}
        />
      </div>

      <DocumentosGeradosTable
        documentosGerados={history.documentosGerados}
        loadingDocumentos={history.loadingDocumentos}
        menuAberto={history.menuAberto}
        setMenuAberto={history.setMenuAberto}
        abrirDialogVerTexto={history.abrirDialogVerTexto}
        abrirDialogAlterar={history.abrirDialogAlterar}
        downloadDocx={history.downloadDocx}
        formatarData={history.formatarData}
      />

      <VerTextoDialog
        dialogVerTexto={history.dialogVerTexto}
        onClose={history.fecharDialogVerTexto}
        onCopiar={history.copiarTextoDialog}
      />

      <AlterarDocumentoDialog
        dialogAlterar={history.dialogAlterar}
        setDialogAlterar={history.setDialogAlterar}
        onSubmeter={history.submeterAlteracao}
        loading={history.loading}
      />

      {dialogAnexo && (
        <AnexarUnificadoDialog
          onClose={() => setDialogAnexo(false)}
          onAnexar={adicionarAnexos}
          codCli={generation.codCliente ? parseInt(generation.codCliente) : null}
          titulo="Anexar Documento ou Áudio"
        />
      )}
    </div>
  );
};

export default GerarDocumentos;
